import { getAttribution, resolveRefCode } from "@/lib/attribution";

export const LEAD_EVENTS = [
  "phone_click",
  "sms_click",
  "email_click",
  "sms_copy",
  "phone_copy",
  "check_date_click",
  "quote_form_view",
  "quote_form_start",
  "generate_lead",
  "limo_widget_message",
  "landing_view",
  "text_us_click",
  "quote_form_submit",
  "corporate_lead",
  "page_view",
  "whatsapp_click",
] as const;
export type LeadEventName = (typeof LEAD_EVENTS)[number];

const SESSION_KEY = "als_session_id";

export function getSessionId(): string {
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "nostorage";
  }
}

export function getDevice(): "mobile" | "desktop" {
  try {
    return window.matchMedia("(pointer: coarse)").matches ? "mobile" : "desktop";
  } catch {
    return "desktop";
  }
}

function str(v: unknown, max = 200): string | undefined {
  return typeof v === "string" && v ? v.slice(0, max) : undefined;
}

export function buildLeadPayload(event: string, params: Record<string, unknown> = {}) {
  const a = getAttribution();
  const detail: Record<string, string> = {};
  for (const k of ["event_date", "event_name", "availability", "occasion", "message_preview", "message_origin", "occasion_choice", "form_name", "cta_label"]) {
    const v = str(params[k], 200);
    if (v) detail[k] = v;
  }
  return {
    event,
    page_path: str(params.page_path) ?? window.location.pathname,
    link_location: str(params.link_location, 50),
    utm_source: a.utm_source,
    utm_medium: a.utm_medium,
    utm_campaign: a.utm_campaign,
    utm_content: a.utm_content,
    utm_term: a.utm_term,
    ref_code: str(params.ref_code, 80) ?? resolveRefCode(),
    gclid: a.gclid,
    referrer: a.referrer,
    landing_page: a.landing_page,
    session_id: getSessionId(),
    device: getDevice(),
    detail: Object.keys(detail).length ? detail : undefined,
  };
}

/** Fire-and-forget POST that survives tel:/sms: page hand-offs. */
export function beacon(url: string, body: unknown) {
  try {
    const json = JSON.stringify(body);
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([json], { type: "application/json" }))) return;
    void fetch(url, { method: "POST", body: json, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {});
  } catch {
    // never break the page
  }
}

export function sendLeadEvent(event: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  if (!(LEAD_EVENTS as readonly string[]).includes(event)) return;
  beacon("/api/public/track", buildLeadPayload(event, params));
}

const PHONE_RE = /(\(?678\)?[\s.-]*431[\s.-]*7222)/;

/** Fires phone_copy when the business number is copied (outside the copy button). */
export function installPhoneCopyTracking(onCopy: () => void): () => void {
  const handler = (e: ClipboardEvent) => {
    const target = e.target as Element | null;
    if (target?.closest?.("[data-sms-copy]")) return;
    const text = window.getSelection()?.toString() ?? "";
    if (PHONE_RE.test(text)) onCopy();
  };
  document.addEventListener("copy", handler);
  return () => document.removeEventListener("copy", handler);
}
